import { useContext, useEffect, useRef, useState } from 'react';
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import PrimaryButton from '../../../../ui/PrimaryButton';
import IfmsModal from '../../../../ui/IfmsModal';
import useTableNavigation from '../../../../ui/tables/useTableNavigation';

import ApplicationContext from '../../../../ApplicationContext';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import useFastStorage, { useTypedFastStorage } from '../../../../hooks/useFastStorage';

import { ApplicableTableSettings, codesAesCarrierList } from '../../../../tableSettings';

import {
  TextAlignLeftRenderer,
  TextRenderer,
} from '../../../../uiControls/SmartTableRenderers';

import SmartTable from '../../../../uiControls/SmartTable';
import SecondaryButton from '../../../../ui/SecondaryButton';
import useFunction from '../../../../hooks/useFunction';
import { Search, isFilterObjectEmpty, resetSearchKeepPaging } from '../../../../filtering';
import InputControlRow from '../../../../ui/layout/InputControlRow';
import TextBox from '../../../../ui/TextBox';
import LeftRightSplit from '../../../../ui/LeftRightSplit';
import CompanyLookupField from '../../../../uiControls/lookup/CompanyLookupField';
import CloseButton from '../../../../ui/CloseButton';
import { JobNames } from '../../../../framework.core/JobNames';
import { CodeSCACListItem } from '../../../../fms.data/models/CodeSCACListItem';
import { CodeScac } from '../../../../fms.core/CodeScac';
import useDirtyCheck from '../../../../hooks/useDirtyCheck2';
import { ApiResult, InsertResultsWithData } from '../../../../framework.core/data/ApiResult';
import { TrackableEntity } from '../../../../fms.core/data/ChangeTrackable';
import { checkPropDirty, getSQLInfoList } from '../../../../fms.core/data/TrackingModule';
import { findSecurityTask, stringsEqualIgnoreCase } from '../../../../util';
import { CheckLookup } from '../../../../fms.data/lookup/CheckLookup';
import { TextValue } from '../../../../framework.core/data/TextValue';
import { FmsSecTask } from '../../../../fms.core/constants';
import useGlobalYesNoDialog from '../../../../hooks/useGlobalYesNoDialog';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import { CarrierInfo, sortingListData } from '../../ReferenceFiles';
import _ from 'lodash';
import CodesFactory from '../CodesFactory';
import useFilterBar from '../../../../hooks/useFilterBar';
import { ListOrderBy } from "../../../common/AbstractListView";
import postal from 'postal';
import BlackInverseButton from '../../../../ui/BlackInverseButton';
import CompanyListExLookupField from '../../../../uiControls/lookup/CompanyListExLookupField';
import { LookupCompany } from '../../../../fms.data/lookup';
import constants from '../../../../constants';
import IFmsReferenceFilesContext from '../../IfmsReferenceFilesContext';
import { Portal } from 'react-portal';
import GreenButton from '../../../../ui/GreenButton';
import { AddIconButton3, DeleteIconButton3, EditIconButton3, ViewIconButton3, RefreshIconButton2 } from '../../../../ui/IconButton';

function RowSpacer() {
  return (
    <div style={{height: '10px'}}>&nbsp;</div>
  );
}

export function CarrierDetails({
  readonly,
  carrier,
  onChange
}: {
  readonly: boolean,
  carrier: CodeScac,
  onChange: (change: Partial<CodeScac>) => void,
}) {

  useEffect(() => {
    if (readonly) {
      return;
    } 
    
    const focusElement = document.getElementById('code-carrier');
    focusElement?.focus();
  }, [readonly]);

  return (
    <div>
      <div style={{float: 'left', marginRight: '10px', width: 'calc(50% - 10px)'}}>
        <RowSpacer />

        <InputControlRow label="Carrier">
          <TextBox name='carrier-name' textId='code-carrier' disabled={readonly} maxLength = {60} value={carrier?.carrierName || ''} onChange={e => onChange({carrierName: e.target.value})} />
        </InputControlRow>

        <InputControlRow label={<span>Code <span style={{color: 'red'}}>*</span></span>}>
          <LeftRightSplit
            left={
              <div style={{marginRight: '10px'}}>
                <TextBox name='carrier-code' disabled={readonly} value={carrier?.code || ''} maxLength = {4} onChange={e => onChange({code: e.target.value})} />
              </div>
            }
            right={
              <div style={{marginLeft: '10px'}}></div>
            }
          />
        </InputControlRow>

        <InputControlRow label="Company">
          <CompanyLookupField
            maxLength={50}
            name='carrier-companyName'
            filterName="all"
            disabled={readonly}
            initialValue={carrier?.companyName ?? ''}
            onTextChange={shortName => onChange({ companyId:0, companyName:shortName})}
            onSelect={company => onChange({ companyId: company.id, companyName: company.shortName ?? '' })}
          />
        </InputControlRow>

        <InputControlRow label="Freight Vendor (Ocean Import)">
          <CompanyListExLookupField
            maxLength={50}
            name='carrier-oiAcctComName'
            initialValue={carrier?.oiAcctComName ?? ''}
            onTextChange={shortName => onChange({ oiAcctCom:0, oiAcctComName:shortName })}
            onSelect={freightVendorOi => { const company = freightVendorOi as LookupCompany; onChange({ oiAcctCom: company.id, oiAcctComName: company.shortName ?? '' })}}
            disabled={readonly}
          />
        </InputControlRow>

        <InputControlRow label="Freight Vendor (Ocean Export)">
          <CompanyListExLookupField
            maxLength={50}
            name='carrier-ooAcctComName'
            initialValue={carrier?.ooAcctComName ?? ''}
            onTextChange={shortName => onChange({ ooAcctCom:0, ooAcctComName:shortName })}
            onSelect={freightVendorOe => { const company = freightVendorOe as LookupCompany; onChange({ ooAcctCom: company.id, ooAcctComName:company.shortName ?? '' })}}
            disabled={readonly}
          />
        </InputControlRow>

      </div>
      <div style={{float: 'left', marginLeft: '10px', width: 'calc(50% - 10px)'}}>
        <RowSpacer />
        
        <LeftRightSplit
          left={
            <div style={{marginRight: '10px'}}>
              <InputControlRow label="Sender ID">
                <TextBox name='carrier-senderId' disabled={readonly} maxLength = {15} value={carrier?.senderId || ''} onChange={e => onChange({senderId: e.target.value})} />
              </InputControlRow>
            </div>
          }
          right={
            <div style={{marginLeft: '10px'}}>
              <InputControlRow label="Receiver ID">
                <TextBox name='carrier-receiverId' disabled={readonly} maxLength = {15} value={carrier?.receiverId || ''} onChange={e => onChange({receiverId: e.target.value})} />
              </InputControlRow>
            </div>
          }
        />

        <InputControlRow label="Customer Code">
          <TextBox name='carrier-customerCode' disabled={readonly} maxLength = {35} value={carrier?.customerCode || ''} onChange={e => onChange({customerCode: e.target.value})}
              onTabNext={() => {
                document.getElementById('code-carrier')?.focus();
                return true;
              }} />
        </InputControlRow>
      </div>
    </div>
  );
}

function formatElement(data: any, index: number, blockStartPage: number, pageSize: number) {
  const indicator = (blockStartPage - 1) * pageSize + (index + 1);

  return {
    ...data,
    companyName: data.company?.shortName ?? '',
    indicator
  };
}

function formatData(data: any, pageNum: number, pageSize: number, pagePerBatch: number) {
  if (!data) return;

  // 블록의 시작 페이지 계산
  const blockStartPage = pageNum - ((pageNum - 1) % pagePerBatch);

  return data.map((d: any, i: number) => formatElement(d, i, blockStartPage, pageSize));
}

function Carriers({
  componentIndex,
  carrierBuyRate,
  setCarrierBuyRate,
  setHideTab
}: {
  componentIndex: number,
  carrierBuyRate: CarrierInfo,
  setCarrierBuyRate: (x: CarrierInfo) => void,
  setHideTab: (x: boolean) => void
}) {
  const pageSize: number = 25;
  const pagePerBatch: number = 10;

  useFunction({invokers: {
    ifmsReferenceFilesSave: save,
    ifmsReferenceFilesCancel: cancelOperation,
  }});
  useFunctionKeys({onEsc: cancelOperation});
  
  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const {selectTableRow, tableNavigationCleanup } = useTableNavigation('codes-carrier-list', onRowSelect, onRowDoubleSelect, ['carrierName'], false, false, false);
  const [pageNum, setPageNum, deletePageNum, pageNumRef] = useTypedFastStorage<number>(`codes-carrier-list-pageum`, 1);

  const [loading, setLoading] = useState(false);
  const [, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData, dataRef] = useFastStorage(`ifms-referenceFiles-carrier-list`, []);

  const [carrier, _setCarrier, removeCarrierFromStorage] = useTypedFastStorage<CodeScac>(`ifms-referenceFiles-carrier-editable`, null);
  const [orderBy, setOrderBy] = useTypedFastStorage<ListOrderBy>(`ifms-referenceFiles-carrier-orderby`, getDefaultOrderBy());

  const totalDataCountRef = useRef(0);
  const headerOrderByText = useRef('');

  const {setCleanState, dirtyCleanup} = useDirtyCheck(`ifms-referenceFiles-carrier-editable`, ['edit', 'view']);
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeScac);
  const [selectedRow, setSelectedRow] = useState(-1);
  const { FilterButton, search } = useFilterBar(
    codesAesCarrierList,
    undefined,
    getDefaultOrderBy(),
    String(componentIndex),
    false,
    setPageNum,
    {
      pageSize: pageSize,
      pagePerBatch: pagePerBatch,
    },
    setOrderBy,
  );

  function getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'carrierName',
      orderByAscending: true,
    };
  }

  function setCarrier(c:Partial<CodeScac>, skipDirty:boolean = false) {
    if (!skipDirty) {
      setHasChanges(true);
    }
    _setCarrier(c);
  }

  function setData(d:Array<CodeScac>) {
    removeData();
    _setData(d);
  }

  async function fetchData(api: any, pageNum: number, forceCallDb: boolean, newItem: any = undefined) {
    setLoading(true);

    const prevPageNum = pageNumRef.current;
    const prevBlockStartPage = prevPageNum ? prevPageNum - ((prevPageNum - 1) % search.pagePerBatch) : null;
    const prevBlockNum = prevBlockStartPage ? Math.floor((prevBlockStartPage - 1) / search.pagePerBatch) : null;

    setPageNum(pageNum);

    const blockStartPage = pageNum - ((pageNum - 1) % search.pagePerBatch);
    const blockNum = Math.floor((blockStartPage - 1) / search.pagePerBatch);

    const isSameBlock = prevBlockNum === blockNum;
    const searchWithHeaderOrder = {
      ...search,
      headerOrderBy: headerOrderByText.current,
    };

    let entities: any[] = [];

    if (!isSameBlock || forceCallDb) {
      const apiResult = await api.getListItems(JobNames.Scac,
        'CodeSCACListItem',
        CodeSCACListItem.calculateSearchParameters(searchWithHeaderOrder),
        blockStartPage,
        0,
        search.pageSize,
        search.pagePerBatch);

      entities = apiResult && apiResult.entities ? apiResult.entities : [];

      if (entities.length > 0) {
        if (newItem) {
          const filteredEntities = entities.filter(e => e.id !== newItem.id);

          let merged = [newItem, ...filteredEntities];

          const maxCount = search.pageSize * search.pagePerBatch;
          if (merged.length > maxCount) {
            merged = merged.slice(0, maxCount);
          }

          entities = merged;
        }
      } 
      totalDataCountRef.current = apiResult?.totalCount ?? 0;
      setData(formatData(entities, pageNum, search.pageSize, search.pagePerBatch));
    }

    setHasChanges(false);
    setLoading(false);
  }

  useEffect(() => {
    if (isFilterObjectEmpty(search) && search.version === 1) reload();
    
    return () => {
      removeData();
      tableNavigationCleanup();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (loading || (isFilterObjectEmpty(search) && search.version === 1)) {
      return;
    }
    reload(true);
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search]
  );

  useEffect(() => {
    if (dataRef.current?.length > 0 && !loading && pageNum) {
      const blockStartPage = pageNum - ((pageNum - 1) % pagePerBatch);
      const indexOffset = (pageNum - blockStartPage) * pageSize;
      setTimeout(() => {
        selectTableRow(indexOffset, true);
        onRowSelect(indexOffset);
      }, 10);
    }
  }, [loading, pageNum]);

  async function onRowSelect(rowIndex: number){
    setSelectedRow(!isNaN(rowIndex) ? rowIndex: -1);
    setHideTab(true);
  }

  async function onRowDoubleSelect(rowIndex:number) {
    if (!dataRef || !dataRef.current) {
      return;
    }

    const apiResult:ApiResult<CodeScac> = await appContext.api.getJobDetail(JobNames.Scac,
      {id: dataRef.current[rowIndex].id });
    if(!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
      return;
    }

    const objScac = {...apiResult.result, edit: true, view: true, editIndex: rowIndex};
    setCleanState(objScac);
    const tEntity = new CodeScac(objScac);
    tEntity.trackable = true;
    setCarrier(tEntity, true);
  }

  async function setCarrierCodeDetail(isView: boolean){
    const apiResult:ApiResult<CodeScac> = await appContext.api.getJobDetail(JobNames.Scac,
      {id: dataRef.current[selectedRow].id });
    if(!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
      return;
    }

    const objScac = {...apiResult.result, edit: true, view: isView, editIndex: selectedRow};
    setCleanState(objScac);
    const tEntity = new CodeScac(objScac);
    tEntity.trackable = true;
    setCarrier(tEntity, true);
  }

  function updateHeaderOrderByText(orderBy: string, orderByAscending: boolean) {
    if (!orderBy) return;
    const orderStr = orderByAscending
      ? `${orderBy} ASC`
      : `${orderBy} DESC`;
    headerOrderByText.current = orderStr;
  }

  async function reload(clearHeaderOrder: boolean = true) {
    if (clearHeaderOrder) {
      if (isFilterObjectEmpty(search)) {
        headerOrderByText.current = '';
        setOrderBy(getDefaultOrderBy());
      }
      else {
        updateHeaderOrderByText(search.orderBy, search.orderByAscending);
        setOrderBy({ orderBy: search.orderBy, orderByAscending: search.orderByAscending });
      }
    }

    await fetchData(appContext.api, 1, true);
  }

  function onHeaderClick(header: any) {
    if (!pageNum) return;

    if (!orderBy || !orderBy.orderBy) return;

    if (header && header.id !== 'indicator') {
      if (header.id === orderBy.orderBy) {
        setOrderBy({ orderByAscending: !orderBy.orderByAscending });
      } else {
        setOrderBy({ orderBy: header.id, orderByAscending: true });
      }

      let newOrderStr = `${header.id} ASC`;

      if (orderBy && orderBy.orderBy === header.id) {
        newOrderStr = orderBy.orderByAscending ? `${header.id} ASC` : `${header.id} DESC`;
      }

      headerOrderByText.current = newOrderStr;
      fetchData(appContext.api, pageNum, true);
    }
  }

  function selectUserForNew() {

    const newCodeModel = new CodeScac();   
    setCarrier(newCodeModel, false);
    setCleanState(newCodeModel);
    
    setCarrier({
      edit : false,
      view: false});
  }

  async function selectUserForEdit(){
    await setCarrierCodeDetail(false);
  }

  async function selectUserForView(){
    await setCarrierCodeDetail(true);
  }
  
  async function selectUserForDelete(){
    const doConfirm = await showGlobalYesNoDialog(
      <div>
        <h3>Do you want to Delete?</h3>
      </div>
    , false, 0, true, 'warning');
    if(doConfirm){
      const apiResult = await appContext.api.deleteJobDetail(JobNames.Scac, { id:dataRef.current[selectedRow].id , pimsLinkId: 0 });
      if (apiResult && apiResult.success) {
        await reload();
      }else
      showGlobalOkDialog(<p>{"Cannot delete"}</p>);
    }else
    return;
  }

  async function selectUserForBuyingRate(){
    setCarrierBuyRate({tbId: dataRef.current[selectedRow].id, 
                      code: dataRef.current[selectedRow].code});
    setHideTab(false);

    return <CodesFactory componentIndex={12} carrierBuyRate={carrierBuyRate} setCarrierBuyRate={setCarrierBuyRate} setHideTab={setHideTab} />;
  }

  function cancelOperation() {
    const filterNode = document.getElementById('ifms-list-screen-filter');
    const channel = postal.channel('ifms-filter');

    if (!filterNode) {
      setHasChanges(false);
      removeCarrierFromStorage();
    } else {
      channel.publish('ifms-filter.dismiss');
    }
    return true;
  }

  async function checkDataAsync(): Promise<boolean>{
    if(carrier?.code?.length === 0){
      await showGlobalOkDialog(<p>{"  * Empty Code"}</p>);
      return false;
    }
    else {
      if(!carrier?.view || !carrier?.edit || checkPropDirty(carrier as TrackableEntity, 'code')){
          if (await appContext.api.checkupDuplicateFieldValueIsDuplicate("T_CODESCAC", "F_Code", carrier?.code, "F_ID", carrier?.id)) {
            await showGlobalOkDialog(<p> Duplicated Value. Please type in another value.</p>);
            return false;
          }
      }
    }
    
    const comLks: CheckLookup[] = [];

    comLks.push(new CheckLookup({
      labelName: "Company",
      textValue: new TextValue({Text: carrier?.companyName, IntValue: carrier?.companyId})
    }))
    comLks.push(new CheckLookup({
      labelName: "Freight Vendor (Ocean Import)",
      textValue: new TextValue({Text: carrier?.oiAcctComName , IntValue: carrier?.oiAcctCom})
    }))
    comLks.push(new CheckLookup({
      labelName: "Freight Vendor (Ocean Export)",
      textValue: new TextValue({Text: carrier?.ooAcctComName, IntValue: carrier?.ooAcctCom})
    }))

    const tempErrMsg = await appContext.api.checkCompanyLookupField(comLks);
    if (tempErrMsg) {
      await showGlobalOkDialog(tempErrMsg);
      return false;
    }

    return true;
  }

  async function save() {
    if (!carrier || !pageNum) {
      return;
    }
   if (!await checkDataAsync()) return;
  
    const sqlInfoList = getSQLInfoList(carrier as TrackableEntity);
    // Replace @ScacId by ct.id
    const sqlInfo = sqlInfoList.find(x => stringsEqualIgnoreCase(x.tableName, 't_aotherinfo'));
    if(sqlInfo && carrier.id > 0) {
      const f = sqlInfo.whereFields.find(w => w.value === '@ScacId');
      if(f) f.value = carrier.id;
    }
    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveJobDetail(
      JobNames.Scac,
      sqlInfoList
    );

    if(!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>{apiResult?.message ?? constants.SAVE_ERROR_MESSAGE}</p>);
      return;
    }
    else {
      await showGlobalOkDialog(<p>The data has been saved</p>);

      if (apiResult.result){
        const insresult = apiResult.result.insertList[0].newId;
        if (insresult) {
          const newItem: any = {
            id: insresult,
            carrierName: carrier.carrierName,
            code: carrier.code,
            senderId: carrier.senderId,
            receiverId: carrier.receiverId,
            customerCode: carrier.customerCode,
            companyId: carrier.companyId,
            sName: carrier.companyName,            
            oiAcctCom: carrier.oiAcctCom,
            oiAcctComName: carrier.oiAcctComName,
            ooAcctCom: carrier.ooAcctCom,
            ooAcctComName: carrier.ooAcctComName,
          };
          await fetchData(appContext.api, 1, true, newItem);
        }
      }
      else {
        const editItem = data.find((x: any) => x.id === carrier.id);
        editItem.carrierName = carrier.carrierName;
        editItem.code = carrier.code;
        editItem.senderId = carrier.senderId;
        editItem.receiverId = carrier.receiverId;
        editItem.customerCode = carrier.customerCode;
        editItem.companyId = carrier.companyId;
        editItem.sName = carrier.companyName;           
        editItem.oiAcctCom = carrier.oiAcctCom;
        editItem.oiAcctComName = carrier.oiAcctComName;
        editItem.ooAcctCom = carrier.ooAcctCom;
        editItem.ooAcctComName = carrier.ooAcctComName;

        const insIndex = data.findIndex((x:any) => x.id === carrier.id);
        const newList = _.cloneDeep(data);
        newList.splice(insIndex, 1, editItem);
        setData(formatData(newList, pageNum, pageSize, pagePerBatch));

        selectTableRow(insIndex);
      }
    }
 
    cancelOperation();
    dirtyCleanup();
  }

  const tableOptions:ApplicableTableSettings = {
    customTableId: 'codes-carrier-list',
    selectable: true,
    tableName: 'codesCarrierList',
    ...codesAesCarrierList,
    height: '522px',
    data: (data ?? []).map((x: any) => ({ ...x })),
    rowRenderers: [
      { accessor: 'indicator', Cell: TextRenderer },
      { accessor: 'carrierName', Cell: TextAlignLeftRenderer },
      { accessor: 'code', Cell: TextRenderer },
      { accessor: 'sName', Cell: TextAlignLeftRenderer },
      { accessor: 'senderId', Cell: TextAlignLeftRenderer },
      { accessor: 'receiverId', Cell: TextAlignLeftRenderer },
      { accessor: 'customerCode', Cell: TextAlignLeftRenderer },
    ],
    orderBy: [orderBy],
    onHeaderClick,
    paging: {
      pageSize: pageSize,
      pageCount: Math.ceil(totalDataCountRef.current / pageSize),
      pagePerBatch: pagePerBatch,
      onPageSelect: (page: number) => {
        fetchData(appContext.api, page, false);
      },
      totalDataCount: totalDataCountRef.current,
      pageNum,
      labelStyle: {
        hide: false,
        isFilterEmpty: isFilterObjectEmpty(search),
        emptyLabel: 'Total',
      },
    },
  };

  const canBeAdd = carrier === null && listSec?.grantAdd;
  const canBeEdit = !isNaN(selectedRow) && !(selectedRow < 0) && listSec?.grantEdit;

  return (
    <div style={{width: '900px', marginTop: '40px'}}>
      <div style={{marginLeft: 'auto'}}></div>
      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{display:'flex', width: '100px'}}>
          <div style={{ margin: 'auto' }}></div>
          <div className="ifms-common-bar-element-center">
            <CloseButton onlyIcon name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
        </div>
      </Portal>
      <div style={{ display: 'flex', width: '864px' }}>
        <div style={{ marginLeft: 'auto' }}></div>
        <div style={{ display:'flex', position: 'relative', top: '-4px' }}>
          <div style={{ display: 'none' }}>
            <FilterButton /> &nbsp;&nbsp;
          </div>
          {appContext.systemSettings?.isCanada ?
            ''
            :
            <SecondaryButton name='codes-carrier-buyingrate-button' size="regular" style={{marginTop :'4px'}} onClick={selectUserForBuyingRate}>Buying Rate</SecondaryButton>
          }
          &nbsp;&nbsp;
          <AddIconButton3 name='codes-carrier-add-button' size="regular" disabled={!canBeAdd} onClick={selectUserForNew}/>&nbsp;&nbsp;
          <EditIconButton3 name='codes-carrier-edit-button' size="regular" disabled={!(canBeEdit && !isNaN(selectedRow) && !(selectedRow < 0))} onClick={selectUserForEdit}/>&nbsp;&nbsp;
          <ViewIconButton3 name='codes-carrier-view-button' size="regular" disabled={isNaN(selectedRow) || selectedRow < 0} onClick={selectUserForView} iconHeight='26px' />&nbsp;&nbsp;
          <DeleteIconButton3 name='codes-carrier-delete-button' size="regular" disabled={isNaN(selectedRow) || selectedRow < 0} onClick={selectUserForDelete}/>&nbsp;
          {/* <BlackInverseButton name='list-screen-refresh' size="large" disabled={loading} onClick={() => { reload(true) }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton> */}
          <RefreshIconButton2 name='list-screen-refresh' onClick={() => { reload(true) }} disabled={loading} data-tooltip="Refresh"/>
        </div>
      </div>
      <div style={{ width: '864px' }}>
        <SmartTable {...tableOptions} />
      </div>

      <RowSpacer />

      <IfmsModal width="800px" isOpen={!!carrier} title={carrier && carrier.edit ? (carrier.view ? 'Carrier View' : 'Carrier Edit') : 'Carrier New'} onCancel={cancelOperation}>
        {!!carrier &&
          <div>
            <div style={{display: 'flex'}}>
              <div style={{marginLeft: 'auto'}}></div>
              <div className="ifms-common-bar-element ifms-right-separator-light-gray">
                {carrier.view && listSec?.grantEdit &&
                  <EditIconButton3 name='carrier-detail-edit-button' onClick={() => setCarrier({view: false}, true)}/>
                }
                {!carrier.view &&
                  <SecondaryButton size='regular' name='carrier-detail-save-button' onClick={save}>Save</SecondaryButton>
                }
                &nbsp;
              </div>
              <div className="ifms-common-bar-element">
                <CloseButton onlyIcon name='carrier-detail-close-button' onClick={cancelOperation} />
              </div>
            </div>
            <CarrierDetails readonly={carrier.view} carrier={carrier} onChange={setCarrier} />
            <div style={{clear: 'both'}}></div>
          </div>
        }
      </IfmsModal>
    </div>
  );
}

export default Carriers;
